import React, { useMemo, useRef, useState } from "react";
import { Button, Checkbox, Col, Input, InputNumber, Row, Tooltip } from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import uuid from "react-uuid";
import GifImageUploader from "./GifImageUploader";
import {
  createEmptyGifState,
  DEFAULT_GIF_SETTINGS,
  GIF_SETTING_LIMITS,
  MIN_GIF_IMAGES,
} from "./gifConstants";
import { generateGifBlob } from "./generateGifBlob";
import { validateGifSettings } from "./validateGifSettings";
import "./GifCreator.scss";

const revokeIfBlob = (url) => {
  if (url && typeof url === "string" && url.startsWith("blob:")) {
    URL.revokeObjectURL(url);
  }
};

const GifCreator = ({ value, onChange, disabled = false }) => {
  const state = useMemo(
    () => ({
      ...createEmptyGifState(),
      ...(value || {}),
      images: value?.images || [],
    }),
    [value]
  );

  const [generating, setGenerating] = useState(false);
  const generatingRef = useRef(false);

  const emit = (patch) => {
    onChange?.({
      ...state,
      ...patch,
    });
  };

  const handleImagesChange = (images) => {
    emit({
      images,
      generatedSignature: "",
    });
  };

  const handleSettingChange = (key, nextValue) => {
    emit({ [key]: nextValue });
  };

  const handleReset = () => {
    (state.images || []).forEach((img) => revokeIfBlob(img.url));
    revokeIfBlob(state.previewUrl);
    onChange?.(createEmptyGifState());
    toast.success("GIF settings and images have been reset.");
  };

  const handleGenerate = async () => {
    if (generatingRef.current || generating || disabled) return;

    const settings = {
      width: Number(state.width),
      height: Number(state.height),
      frameDelay: Number(state.frameDelay),
      loopCount: Number(state.loopCount),
      crossfade: !!state.crossfade,
      crossfadeSteps: Number(state.crossfadeSteps),
      background: state.background || DEFAULT_GIF_SETTINGS.background,
    };

    const validationError = validateGifSettings({
      images: state.images,
      settings,
    });

    if (validationError) {
      toast.error(validationError);
      return;
    }

    generatingRef.current = true;
    setGenerating(true);
    emit({ generating: true });

    try {
      const blob = await generateGifBlob({
        images: state.images,
        ...settings,
      });

      const mediaId = uuid();
      const fileName = `${mediaId}_generated-gif.gif`;
      const generatedFile = new File([blob], fileName, { type: "image/gif" });
      const previewUrl = URL.createObjectURL(blob);

      revokeIfBlob(state.previewUrl);

      emit({
        ...settings,
        generating: false,
        previewUrl,
        generatedFile,
        fileName,
        mimeType: "image/gif",
        fileSize: blob.size,
        mediaProperty: {
          id: mediaId,
          name: `${mediaId}_generated-gif.gif`,
          extension: "gif",
        },
        generatedSignature: (state.images || []).map((img) => img.uid).join("|"),
        isEditing: false,
        isUnsaved: true,
      });

      toast.success("GIF generated successfully.");
    } catch (error) {
      emit({ generating: false });
      toast.error(
        error?.message || "GIF generation failed. Please check your images and try again."
      );
    } finally {
      generatingRef.current = false;
      setGenerating(false);
    }
  };

  const showEditor = state.isEditing !== false || !state.previewUrl;
  const generateDisabled =
    disabled ||
    generating ||
    (state.images || []).length < MIN_GIF_IMAGES;

  return (
    <div className="gif-creator">
      {showEditor ? (
        <>
          <div className="gif-creator-block">
            <h4>Images</h4>
            <GifImageUploader value={state.images} onChange={handleImagesChange} />
          </div>

          <div className="gif-creator-block gif-creator-settings">
            <h4>GIF Configuration</h4>

            <Row align="middle" gutter={[0, 8]} className="gif-creator-setting-row">
              <Col xs={24} sm={8}>
                <label htmlFor="gif-width">GIF Width</label>
              </Col>
              <Col xs={24} sm={4} className="gif-creator-input-col">
                <InputNumber
                  id="gif-width"
                  className="gif-creator-input"
                  min={GIF_SETTING_LIMITS.width.min}
                  max={GIF_SETTING_LIMITS.width.max}
                  step={1}
                  precision={0}
                  value={state.width}
                  disabled={disabled || generating}
                  onChange={(val) => handleSettingChange("width", val)}
                />
              </Col>
              <Col xs={24} sm={12} className="gif-creator-help-col">
                <p className="gif-creator-help">
                  Output width in pixels ({GIF_SETTING_LIMITS.width.min}–{GIF_SETTING_LIMITS.width.max}).
                </p>
              </Col>
            </Row>

            <Row align="middle" gutter={[0, 8]} className="gif-creator-setting-row">
              <Col xs={24} sm={8}>
                <label htmlFor="gif-height">GIF Height</label>
              </Col>
              <Col xs={24} sm={4} className="gif-creator-input-col">
                <InputNumber
                  id="gif-height"
                  className="gif-creator-input"
                  min={GIF_SETTING_LIMITS.height.min}
                  max={GIF_SETTING_LIMITS.height.max}
                  step={1}
                  precision={0}
                  value={state.height}
                  disabled={disabled || generating}
                  onChange={(val) => handleSettingChange("height", val)}
                />
              </Col>
              <Col xs={24} sm={12} className="gif-creator-help-col">
                <p className="gif-creator-help">
                  Output height in pixels ({GIF_SETTING_LIMITS.height.min}–{GIF_SETTING_LIMITS.height.max}).
                </p>
              </Col>
            </Row>

            <Row align="middle" gutter={[0, 8]} className="gif-creator-setting-row">
              <Col xs={24} sm={8}>
                <label htmlFor="gif-delay">Frame Delay (ms)</label>
              </Col>
              <Col xs={24} sm={4} className="gif-creator-input-col">
                <InputNumber
                  id="gif-delay"
                  className="gif-creator-input"
                  min={GIF_SETTING_LIMITS.frameDelay.min}
                  max={GIF_SETTING_LIMITS.frameDelay.max}
                  step={10}
                  precision={0}
                  value={state.frameDelay}
                  disabled={disabled || generating}
                  onChange={(val) => handleSettingChange("frameDelay", val)}
                />
              </Col>
              <Col xs={24} sm={12} className="gif-creator-help-col">
                <p className="gif-creator-help">
                  How long each image is shown before the next frame (milliseconds).
                </p>
              </Col>
            </Row>

            <Row align="middle" gutter={[0, 8]} className="gif-creator-setting-row">
              <Col xs={24} sm={8}>
                <label htmlFor="gif-loops">Number of Loops</label>
              </Col>
              <Col xs={24} sm={4} className="gif-creator-input-col">
                <InputNumber
                  id="gif-loops"
                  className="gif-creator-input"
                  min={GIF_SETTING_LIMITS.loopCount.min}
                  max={GIF_SETTING_LIMITS.loopCount.max}
                  step={1}
                  precision={0}
                  value={state.loopCount}
                  disabled={disabled || generating}
                  onChange={(val) => handleSettingChange("loopCount", val)}
                />
              </Col>
              <Col xs={24} sm={12} className="gif-creator-help-col">
                <p className="gif-creator-help">Use 0 for continuous looping.</p>
              </Col>
            </Row>

            <Row align="middle" gutter={[0, 8]} className="gif-creator-setting-row">
              <Col xs={24} sm={8}>
                <label htmlFor="gif-crossfade">Enable Crossfade Transition</label>
              </Col>
              <Col xs={24} sm={4} className="gif-creator-input-col">
                <Checkbox
                  id="gif-crossfade"
                  checked={!!state.crossfade}
                  disabled={disabled || generating}
                  onChange={(e) => handleSettingChange("crossfade", e.target.checked)}
                />
              </Col>
              <Col xs={24} sm={12} className="gif-creator-help-col">
                <p className="gif-creator-help">Smoothly blend one image into the next.</p>
              </Col>
            </Row>

            {state.crossfade && (
              <Row align="middle" gutter={[0, 8]} className="gif-creator-setting-row">
                <Col xs={24} sm={8}>
                  <label htmlFor="gif-crossfade-steps">Crossfade Steps</label>
                </Col>
                <Col xs={24} sm={4} className="gif-creator-input-col">
                  <InputNumber
                    id="gif-crossfade-steps"
                    className="gif-creator-input"
                    min={GIF_SETTING_LIMITS.crossfadeSteps.min}
                    max={GIF_SETTING_LIMITS.crossfadeSteps.max}
                    step={1}
                    precision={0}
                    value={state.crossfadeSteps}
                    disabled={disabled || generating}
                    onChange={(val) => handleSettingChange("crossfadeSteps", val)}
                  />
                </Col>
                <Col xs={24} sm={12} className="gif-creator-help-col">
                  <p className="gif-creator-help">
                    Higher values create a smoother transition ({GIF_SETTING_LIMITS.crossfadeSteps.min}–{GIF_SETTING_LIMITS.crossfadeSteps.max}).
                  </p>
                </Col>
              </Row>
            )}

            <Row align="middle" gutter={[0, 8]} className="gif-creator-setting-row">
              <Col xs={24} sm={8}>
                <label htmlFor="gif-background">Background</label>
              </Col>
              <Col xs={24} sm={4} className="gif-creator-input-col">
                <div className="gif-creator-background">
                  <Input
                    id="gif-background"
                    type="color"
                    value={state.background || DEFAULT_GIF_SETTINGS.background}
                    disabled={disabled || generating}
                    onChange={(e) => handleSettingChange("background", e.target.value)}
                  />
                  <Tooltip title="Used to fill unused space so images keep their original aspect ratio.">
                    <QuestionCircleOutlined />
                  </Tooltip>
                </div>
              </Col>
              <Col xs={24} sm={12} className="gif-creator-help-col">
                <p className="gif-creator-help">Letterbox colour for images that do not match the GIF size.</p>
              </Col>
            </Row>
          </div>

          <div className="gif-creator-actions">
            <Button
              className="greenBtn"
              type="button"
              loading={generating}
              disabled={generateDisabled}
              onClick={handleGenerate}
            >
              Generate GIF
            </Button>
            <Button
              className="cancelBtn"
              type="button"
              disabled={disabled || generating}
              onClick={handleReset}
            >
              Reset
            </Button>
          </div>
        </>
      ) : (
        <div className="gif-creator-generated-note">
          <p>GIF generated. Use Preview, Edit or Download on the right. Click Edit to change images or settings and generate again.</p>
          <div className="gif-creator-actions">
            <Button
              className="greenBtn"
              type="button"
              disabled={disabled}
              onClick={() => emit({ isEditing: true })}
            >
              Edit
            </Button>
            <Button
              className="cancelBtn"
              type="button"
              disabled={disabled || generating}
              onClick={handleReset}
            >
              Reset
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GifCreator;
